import { isAxiosError } from "axios"
import { useEffect, useId, useState } from "react"
import { getKnowledgeBasesRequest, updateKnowledgeBaseOrderRequest } from "../../api/knowledge-base"
import { KnowledgeBase } from "../../api"
import { useMsal } from "@azure/msal-react"
import { Button, Spinner, Text, Tooltip, makeStyles, shorthands } from "@fluentui/react-components"
import { KnowledgeBaseCardSelectable } from "../knowledge-base-card-selectable/knowledge-base-card-selectable"
import { useLocation, useNavigate } from "react-router-dom"
import { ArrowDownRegular, ArrowUpRegular } from "@fluentui/react-icons"
import { screenReaderSpeak } from "../../helpers/screen-reader-speak"

const useStyles = makeStyles({
    list: {
        ...shorthands.padding(0),
        ...shorthands.margin(0),
        flexDirection: 'column',
        display: 'flex',
        ...shorthands.gap('16px'),
        minHeight: '300px',
        listStyleType: 'none'
    },
    scrollable: {
        overflow: 'hidden auto',
        height: '100%'
    },
    listItem: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap('4px'),
        minWidth: '0px',
    },
    cardContainer: {
        flexGrow: 1,
        minWidth: '0px',
    },
    orderControls: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        ...shorthands.gap('2px'),
    },
    orderButton: {
        minWidth: '28px',
        width: '28px',
        height: '28px',
    }
})

export const KnowledgeBases = ({
    selectedKnowledgeBaseId,
    knowledgeBaseListId,
    isCardFooterVisible = true,
    isReorderModeEnabled = false,
    canCreateKnowledgeBase = false,
    canCreateQuestionExamples = false,
    onKnowledgeBaseSelected,
}: {
    selectedKnowledgeBaseId?: string;
    knowledgeBaseListId?: string;
    isCardFooterVisible?: boolean;
    isReorderModeEnabled?: boolean;
    canCreateKnowledgeBase?: boolean;
    canCreateQuestionExamples?: boolean;
    onKnowledgeBaseSelected?: () => void;
}) => {
    const styles = useStyles();
    const { instance } = useMsal();
    const navigate = useNavigate();
    const { pathname } = useLocation()
    const generatedKnowledgeBaseListId = useId();
    const reorderInstructionsId = useId();
    const listId = knowledgeBaseListId || generatedKnowledgeBaseListId;

    const activeAccount = instance.getActiveAccount();
    // const { knowledgeBase: selectedKnowledgeBase, selectKnowledgeBase } = useKnowledgeBase()

    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([])
    const [isKnowledgeBasesLoading, setIsKnowledgeBasesLoading] = useState<boolean>(false)
    const [isKnowledgeBaseOrderSaving, setIsKnowledgeBaseOrderSaving] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [orderError, setOrderError] = useState<string | null>(null)

    const onSelectKnowledgeBase = (knowledgeBaseId: string) => {
        onKnowledgeBaseSelected?.();
        navigate(`/chat/${knowledgeBaseId}`)
    }

    // const toggleFooterVisibility = () => {
    //     setFooterVisible(!isCardFooterVisible);
    // };

    const getVisibleKnowledgeBases = (allKnowledgeBases: KnowledgeBase[]) => {
        return canCreateKnowledgeBase || canCreateQuestionExamples
            ? allKnowledgeBases
            : allKnowledgeBases.filter(kb => kb.config.published);
    };

    useEffect(() => {
        const getKnowledgeBases = async () => {
            try {
                setIsKnowledgeBasesLoading(true)

                const knowledgeBases = await getKnowledgeBasesRequest()
                const allKnowledgeBases = knowledgeBases.data;
                const visibleKnowledgeBases = getVisibleKnowledgeBases(allKnowledgeBases);

                setKnowledgeBases(visibleKnowledgeBases);
                setError(null);
            } catch (error: unknown) {
                if (isAxiosError(error)) {
                    if (error.response?.data?.status === 401) {
                        instance.logout()
                    } else if (error.response?.data?.message) {
                        setError(error.response?.data?.message)
                    } else {
                        setError('An error occurred while fetching knowledge bases')
                    }
                }
            } finally {
                setIsKnowledgeBasesLoading(false)
            }
        }

        getKnowledgeBases()
    }, [activeAccount?.idTokenClaims?.oid, canCreateKnowledgeBase, canCreateQuestionExamples])

    const moveKnowledgeBase = async (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (
            targetIndex < 0 ||
            targetIndex >= knowledgeBases.length ||
            isKnowledgeBaseOrderSaving
        ) {
            return;
        }

        const previousKnowledgeBases = knowledgeBases;
        const nextKnowledgeBases = [...knowledgeBases];
        [nextKnowledgeBases[index], nextKnowledgeBases[targetIndex]] = [
            nextKnowledgeBases[targetIndex],
            nextKnowledgeBases[index],
        ];
        const movedKnowledgeBase = nextKnowledgeBases[targetIndex];

        setKnowledgeBases(nextKnowledgeBases);
        setOrderError(null);
        screenReaderSpeak(`${movedKnowledgeBase.name} moved to position ${targetIndex + 1} of ${nextKnowledgeBases.length}. Saving Knowledge Base order.`, 'polite');
        setIsKnowledgeBaseOrderSaving(true);

        try {
            const response = await updateKnowledgeBaseOrderRequest({
                knowledgeBaseIds: nextKnowledgeBases.map(knowledgeBase => knowledgeBase.id),
            });
            const savedKnowledgeBases = getVisibleKnowledgeBases(response.data);
            const savedPosition = savedKnowledgeBases.findIndex(knowledgeBase => knowledgeBase.id === movedKnowledgeBase.id) + 1;
            const defaultKnowledgeBase = savedKnowledgeBases[0];

            setKnowledgeBases(savedKnowledgeBases);
            screenReaderSpeak(`${movedKnowledgeBase.name} moved to position ${savedPosition || targetIndex + 1} of ${savedKnowledgeBases.length}. Knowledge Base order saved. ${defaultKnowledgeBase.name} is the default Knowledge Base.`, 'polite');
        } catch (error: unknown) {
            const errorMessage = isAxiosError(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Unable to save knowledge base order';

            setKnowledgeBases(previousKnowledgeBases);
            // The visible orderError below is rendered with role="alert", so it announces itself.
            setOrderError(errorMessage);
        } finally {
            setIsKnowledgeBaseOrderSaving(false);
        }
    };

    // Load the organization's top ordered knowledge base on default routes.
    useEffect(() => {
        const normalizedPathname = pathname.endsWith('/') && pathname !== '/'
            ? pathname.slice(0, -1)
            : pathname;
        const shouldLoadDefaultKnowledgeBase =
            !selectedKnowledgeBaseId &&
            (normalizedPathname === '/' || normalizedPathname === '/chat');

        if (
            knowledgeBases.length > 0 &&
            shouldLoadDefaultKnowledgeBase
        ) {
            navigate(`/chat/${knowledgeBases[0].id}`, { replace: true });
        }
    }, [knowledgeBases, pathname, selectedKnowledgeBaseId, navigate]);

    if (isKnowledgeBasesLoading) return <Spinner label="Loading" tabIndex={0} aria-live="polite" />

    if (error) return <Text>{error}</Text>

    if (knowledgeBases.length === 0) return <Text>You don't have any active knowledge bases. Ask your administrator to create them for you</Text>

    return (
        <>
        {isReorderModeEnabled && (
            <span id={reorderInstructionsId} className="sr-only">
                Knowledge Base reorder mode is active. Use the Move up and Move down buttons to change the organization-wide display order.
            </span>
        )}
        <ul
            id={listId}
            className={`${styles.list} ${styles.scrollable}`}
            aria-label={isReorderModeEnabled ? "Knowledge bases, reorder mode active" : "Knowledge bases"}
            aria-describedby={isReorderModeEnabled ? reorderInstructionsId : undefined}
            aria-busy={isKnowledgeBaseOrderSaving}
        >
            {isReorderModeEnabled && orderError && (
                <li>
                    <Text role="alert" aria-live="assertive">{orderError}</Text>
                </li>
            )}
            {knowledgeBases.map((knowledgeBase, index) => (
                <li
                    key={knowledgeBase.id}
                    className={styles.listItem}
                    aria-posinset={index + 1}
                    aria-setsize={knowledgeBases.length}
                >
                    <div className={styles.cardContainer}>
                        <KnowledgeBaseCardSelectable
                            id={knowledgeBase.id}
                            title={knowledgeBase.name}
                            documentCount={knowledgeBase.documentCount}
                            updatedAt={knowledgeBase.updatedAt}
                            published={knowledgeBase.config.published}
                            citation_view={knowledgeBase.config.citation_view}
                            subtitle={knowledgeBase.description}
                            selected={knowledgeBase.id === selectedKnowledgeBaseId && !pathname.includes('administration')}
                            onClick={() => onSelectKnowledgeBase(knowledgeBase.id)}
                            canCreateKnowledgeBase={canCreateKnowledgeBase}
                            canCreateQuestionExamples={canCreateQuestionExamples}
                            isFooterVisible={isCardFooterVisible}
                        />
                    </div>
                    {canCreateKnowledgeBase && isReorderModeEnabled && (
                        <div className={styles.orderControls}>
                            <Tooltip content={`Move ${knowledgeBase.name} up`} relationship="label" positioning="after">
                                <Button
                                    appearance="transparent"
                                    aria-label={`Move ${knowledgeBase.name} up from position ${index + 1} of ${knowledgeBases.length}`}
                                    aria-controls={listId}
                                    className={styles.orderButton}
                                    disabled={index === 0 || isKnowledgeBaseOrderSaving}
                                    icon={<ArrowUpRegular />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        moveKnowledgeBase(index, -1);
                                    }}
                                    shape="circular"
                                    size="small"
                                />
                            </Tooltip>
                            <Tooltip content={`Move ${knowledgeBase.name} down`} relationship="label" positioning="after">
                                <Button
                                    appearance="transparent"
                                    aria-label={`Move ${knowledgeBase.name} down from position ${index + 1} of ${knowledgeBases.length}`}
                                    aria-controls={listId}
                                    className={styles.orderButton}
                                    disabled={index === knowledgeBases.length - 1 || isKnowledgeBaseOrderSaving}
                                    icon={<ArrowDownRegular />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        moveKnowledgeBase(index, 1);
                                    }}
                                    shape="circular"
                                    size="small"
                                />
                            </Tooltip>
                        </div>
                    )}
                </li>
            ))}
        </ul>
        </>
    )
}
